/**
 * Paste-to-fix — which pasted characters won't compile, and what the chip can
 * honestly offer for each. (SPEC-symbols-equations §6, as revised by Olivia on
 * 25 Sep in REPLY-to-lilia-2026-09-25 §1.)
 *
 * The classification is the server's (`POST /api/unicode/check`), so the chip
 * gives the same answer as the compile. Only `wontCompile` is spoken to: the
 * shimmed characters already compile in Lilia, and a chip about them would be
 * false.
 */

export type CharKind = "symbol" | "script" | "emoji";

export interface WontCompileChar {
  char: string;
  codepoint: number;
  kind: CharKind;
}

export interface UnicodeCheck {
  /** False when the server could not check: nothing below means "clean". */
  checked: boolean;
  wontCompile: WontCompileChar[];
}

const KINDS: readonly string[] = ["symbol", "script", "emoji"];

/**
 * The API omits nulls and an older API sends no `kind`. Without one, Olivia's
 * fallback holds: offer *Look up* for everything — a dead-end lookup is a worse
 * experience, but not a false statement.
 */
export function normalizeCheck(raw: unknown): UnicodeCheck {
  const r = (raw ?? {}) as { checked?: unknown; wontCompile?: unknown };
  const list = Array.isArray(r.wontCompile) ? r.wontCompile : [];
  return {
    checked: r.checked === true,
    wontCompile: list
      .filter((c): c is { char: string; codepoint: number; kind?: unknown } =>
        typeof c?.char === "string" && typeof c?.codepoint === "number")
      .map((c) => ({
        char: c.char,
        codepoint: c.codepoint,
        kind: (KINDS.includes(c.kind as string) ? c.kind : "symbol") as CharKind,
      })),
  };
}

/**
 * What to send: each distinct non-ASCII character once. The server only
 * classifies; counting happens here, over the real paste — so a whole document
 * can be checked on load without sending the document.
 */
export function distinctNonAscii(text: string): string {
  const seen = new Set<string>();
  for (const ch of text) if (ch.codePointAt(0)! > 0x7f) seen.add(ch);
  return [...seen].join("");
}

export function countOccurrences(text: string, chars: Iterable<string>): number {
  const wanted = new Set(chars);
  let n = 0;
  for (const ch of text) if (wanted.has(ch)) n++;
  return n;
}

export interface PasteChip {
  /** *N pasted characters won't compile.* Fixed and literal (§6). */
  headline: string;
  /**
   * The characters themselves, in the order they were pasted. "Other scripts"
   * is only clear because the author sees 中 等 beside it (Olivia, 25 Sep) — the
   * copy depends on this list, in every variant.
   */
  chars: string[];
  /** How many more distinct characters than `chars` shows. */
  moreChars: number;
  /** Symbols that might have a command: one *Look up* each. */
  lookUps: string[];
  /** For script and emoji, what's true — no action, because none exists. */
  noSymbolLine: string | null;
}

/** At most this many *Look up* buttons; a chip is not a list. */
export const MAX_LOOKUPS = 3;

/** Enough to recognise the text; a paragraph of Chinese is not listed whole. */
export const MAX_LISTED = 8;

export function chipFor(pasted: string, check: UnicodeCheck): PasteChip | null {
  if (!check.checked || check.wontCompile.length === 0) return null;

  const count = countOccurrences(pasted, check.wontCompile.map((c) => c.char));
  if (count === 0) return null;

  const kinds = new Set(check.wontCompile.map((c) => c.kind));
  return {
    headline: `${count} pasted ${count === 1 ? "character" : "characters"} won't compile.`,
    ...listed(pasted, new Set(check.wontCompile.map((c) => c.char))),
    lookUps: check.wontCompile.filter((c) => c.kind === "symbol").map((c) => c.char).slice(0, MAX_LOOKUPS),
    noSymbolLine: noSymbolLine(kinds.has("script"), kinds.has("emoji")),
  };
}

function listed(pasted: string, failing: ReadonlySet<string>): { chars: string[]; moreChars: number } {
  const inOrder: string[] = [];
  for (const ch of pasted) if (failing.has(ch) && !inOrder.includes(ch)) inOrder.push(ch);
  return { chars: inOrder.slice(0, MAX_LISTED), moreChars: Math.max(0, inOrder.length - MAX_LISTED) };
}

function noSymbolLine(script: boolean, emoji: boolean): string | null {
  if (script && emoji) return "Text in other scripts and emoji need a different document setup — there's no symbol for them.";
  if (script) return "Text in other scripts needs a different document setup — there's no symbol for it.";
  if (emoji) return "Emoji need a different document setup — there's no symbol for them.";
  return null;
}
